import { useMemo, useRef, useState } from 'react';
import {
  buildTuningUrl,
  deriveStrings,
  idealBassOctaveFor,
  useFlashStore,
  useTuningStore,
  useUiStore,
  type DerivedString,
  type TuningSet,
} from '@store/index';
import { getPresetByKey } from '@engine/scale';
import { StringRulerRow } from './StringRulerRow';
import { MicMode } from './MicMode';
import { PresetGrid } from './PresetGrid';
import { ScaleCircle } from './ScaleCircle';
import { useAudioEngine } from '@hooks/index';
import './TuningPanel.css';

const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const MIN_STRINGS = 5;
const MAX_STRINGS = 25;

function noteToMidi(pc: string, octave: number): number {
  const idx = NOTE_NAMES.indexOf(pc.toUpperCase());
  return (octave + 1) * 12 + (idx >= 0 ? idx : 0);
}

/**
 * All-visual tuning panel. No dropdowns, no forms — every control is a
 * tile, a ruler, or a coloured dot. Left-to-right flow of direct
 * manipulation:
 *   1. Sets — visual cards.
 *   2. Preset gallery — each preset is a card with a mini ruler showing
 *      its interval shape, so the player recognizes "this one" without
 *      knowing names like "Phrygian" or "octatonic".
 *   3. Scale frame — the big live visualization. Drag empty track to
 *      transpose, tap a dot to rotate it to the root, drag a dot for
 *      microtonal adjustment.
 *   4. Strings — each string is a coloured dot on its own horizontal
 *      pitch ruler. Colour = scale degree, so "same-colour strings
 *      move together" is visually obvious.
 *   5. +/− buttons on the strings list edit the string count.
 * Everything else is collapsed into Advanced.
 */
export function TuningPanel() {
  const sets = useTuningStore((s) => s.sets);
  const activeIndex = useTuningStore((s) => s.activeIndex);
  const setActive = useTuningStore((s) => s.setActive);
  const addSet = useTuningStore((s) => s.addSet);
  const removeSet = useTuningStore((s) => s.removeSet);
  const renameSet = useTuningStore((s) => s.renameSet);
  const applyPreset = useTuningStore((s) => s.applyPreset);
  const updateSet = useTuningStore((s) => s.updateSet);
  const freeCents = useTuningStore((s) => s.advancedMode);
  const setFreeCents = useTuningStore((s) => s.setAdvancedMode);
  const setStringOverride = useTuningStore((s) => s.setStringOverride);

  const [editingName, setEditingName] = useState<number | null>(null);
  const [armedStringIndex, setArmedStringIndex] = useState<number | null>(null);
  const { ensureStarted } = useAudioEngine();
  const flashString = useUiStore((s) => s.flashString);
  const strictAscending = useUiStore((s) => s.strictAscending);
  const setStrictAscending = useUiStore((s) => s.setStrictAscending);
  const stringSpacing = useUiStore((s) => s.stringSpacing);
  const setStringSpacing = useUiStore((s) => s.setStringSpacing);
  const stringOrderLowTop = useUiStore((s) => s.stringOrderLowTop);
  const setStringOrderLowTop = useUiStore((s) => s.setStringOrderLowTop);
  const flashPluck = useFlashStore((s) => s.flash);
  const previewTimers = useRef<number[]>([]);
  const [previewPlaying, setPreviewPlaying] = useState(false);

  const active = sets[activeIndex]!;
  const derived = useMemo(() => deriveStrings(active), [active]);
  const ordered = useMemo(
    () => (stringOrderLowTop ? derived : [...derived].reverse()),
    [derived, stringOrderLowTop],
  );
  // Auto-fit the strings ruler to the actual pitch span of the set, so
  // nothing clusters at the edges. Snap range to octave boundaries for
  // clean C-labels. 2-semitone margins inside that.
  const { rulerMinMidi, rulerMaxMidi } = useMemo(() => {
    if (derived.length === 0) return { rulerMinMidi: 36, rulerMaxMidi: 84 };
    let lo = Infinity;
    let hi = -Infinity;
    for (const d of derived) {
      if (d.midi < lo) lo = d.midi;
      if (d.midi > hi) hi = d.midi;
    }
    const padLo = lo - 2;
    const padHi = hi + 2;
    const alignedLo = padLo - ((padLo % 12) + 12) % 12; // snap down to C
    const alignedHi = padHi + ((12 - ((padHi % 12) + 12) % 12) % 12); // snap up to C
    return { rulerMinMidi: alignedLo, rulerMaxMidi: alignedHi };
  }, [derived]);

  const retuneDegree = (
    stringIndex: number,
    pitchClass: string,
    octave: number,
    cents: number,
  ) => {
    if (!strictAscending) {
      // Free mode → one string deviates, the scale/circle stay.
      setStringOverride(activeIndex, stringIndex, { pitchClass, octave, cents });
      return;
    }
    // Strict mode → drag moves the scale. Map string index to scale
    // degree + cycle, then either retune R (if the dragged string IS a
    // root-degree string at any octave) or retune that scale degree's
    // interval from R.
    const size = Math.max(1, active.scaleSize);
    const lowIdx = active.lowestDegreeIdx ?? 0;
    const rawIdx = lowIdx + stringIndex;
    const degree = rawIdx % size;
    const cycleRel = Math.floor(rawIdx / size);
    const periodSemi = Math.max(1, Math.round(active.scalePeriod / 100));
    const targetMidi = noteToMidi(pitchClass, octave);
    const overrides = { ...active.overrides };
    if (overrides[stringIndex]) delete overrides[stringIndex];

    if (degree === 0) {
      // Any root string (red) — all root strings are R at different
      // cycles, so they must move together. Back out this particular
      // string's cycle to recover R's reference pitch, then re-derive
      // bassOctave so the lowest string lands in the right register.
      const rootMidiAtZero = Math.max(
        24,
        Math.min(108, targetMidi - cycleRel * periodSemi),
      );
      const newRootChroma = ((rootMidiAtZero % 12) + 12) % 12;
      const newRootPc = NOTE_NAMES[newRootChroma]!;
      const newBassOctave = Math.max(
        1,
        Math.min(8, Math.floor(rootMidiAtZero / 12) - 1),
      );
      updateSet(activeIndex, {
        bassPitchClass: newRootPc,
        bassOctave: newBassOctave,
        bassCents: cents,
        overrides,
      });
      return;
    }

    // Non-root degree — retune that degree's interval from R. Use the
    // current R midi (at cycleRel=0) to compute the new semitone offset.
    const rootIdxChrom = NOTE_NAMES.indexOf(active.bassPitchClass.toUpperCase());
    const lowestSemi = active.scaleNotes[lowIdx]?.semitones ?? 0;
    // Current lowest MIDI (matches deriveStrings math).
    const curLowestChromaSemi = (rootIdxChrom + lowestSemi) % 12;
    const curLowestExtraOct = Math.floor((rootIdxChrom + lowestSemi) / 12);
    const curLowestMidi =
      (active.bassOctave + 1 + curLowestExtraOct) * 12 + curLowestChromaSemi;
    const curRootMidiAtZero = curLowestMidi - lowestSemi;
    const relSemi = targetMidi - curRootMidiAtZero - cycleRel * periodSemi;
    const nextNotes = active.scaleNotes.map((n, i) =>
      i === degree ? { semitones: relSemi, cents } : n,
    );
    updateSet(activeIndex, { scaleNotes: nextNotes, overrides });
  };

  const setBass = (midi: number, cents: number) => {
    const clamped = Math.max(24, Math.min(108, midi));
    const pc = NOTE_NAMES[((clamped % 12) + 12) % 12]!;
    const oct = Math.floor(clamped / 12) - 1;
    updateSet(activeIndex, {
      bassPitchClass: pc,
      bassOctave: oct,
      bassCents: cents,
    });
  };

  const setLowestDegree = (degreeIdx: number) => {
    const clamped = Math.max(0, Math.min(active.scaleSize - 1, degreeIdx));
    updateSet(activeIndex, { lowestDegreeIdx: clamped });
  };

  const setLowestOctave = (oct: number) => {
    updateSet(activeIndex, { bassOctave: Math.max(1, Math.min(8, oct)) });
  };

  const setDegreeInterval = (degreeIdx: number, totalCents: number) => {
    const semi = Math.floor(totalCents / 100);
    const cents = totalCents - semi * 100;
    const next = active.scaleNotes.map((n, i) =>
      i === degreeIdx ? { semitones: semi, cents } : n,
    );
    updateSet(activeIndex, { scaleNotes: next });
  };

  const changeStringCount = (delta: 1 | -1) => {
    const next = Math.max(
      MIN_STRINGS,
      Math.min(MAX_STRINGS, active.stringCount + delta),
    );
    updateSet(activeIndex, { stringCount: next });
  };

  /** Shift the lowest-string register so the middle of the strings
   *  lands near MIDI 66 (≈ F#4) — the centre of comfortable hearing.
   *  Explicit button instead of auto-fire so it doesn't override
   *  whatever register the player deliberately picked. */
  const centerToComfortRange = () => {
    const target = idealBassOctaveFor(
      active.bassPitchClass,
      active.scaleSize,
      active.stringCount,
      active.scalePeriod,
    );
    if (target === active.bassOctave) return;
    updateSet(activeIndex, { bassOctave: target });
  };

  const randomizeScale = () => {
    const n = active.scaleSize;
    const period = active.scalePeriod;
    const MIN_GAP = Math.max(40, period / (n * 4));
    const positions: number[] = [0];
    let guard = 0;
    while (positions.length < n && guard < 400) {
      guard++;
      const c = Math.random() * (period - MIN_GAP) + MIN_GAP;
      if (positions.every((p) => Math.abs(p - c) > MIN_GAP && Math.abs(period - c) > MIN_GAP)) {
        positions.push(c);
      }
    }
    positions.sort((a, b) => a - b);
    const next = positions.slice(0, n).map((c) => {
      const semi = Math.floor(c / 100);
      const cents = c - semi * 100;
      return { semitones: semi, cents };
    });
    updateSet(activeIndex, { scaleNotes: next });
  };

  /** Snap each degree to a grid. Primary grid (coarse) is tried first;
   *  any degree whose coarse slot is already taken falls back to the
   *  finer grid, picking the nearest unused point. Guarantees no two
   *  degrees collide. */
  const cascadingSnap = (primary: number, fallback: number) => {
    const period = active.scalePeriod;
    const totals = active.scaleNotes.map((n) => n.semitones * 100 + n.cents);
    const used = new Set<number>();
    const snapped: number[] = new Array(totals.length);
    // Root is always 0 — claim it up front so other degrees can't take it.
    used.add(0);
    snapped[0] = 0;
    for (let i = 1; i < totals.length; i++) {
      const t = totals[i]!;
      let candidate = Math.round(t / primary) * primary;
      candidate = Math.max(primary, Math.min(period - primary, candidate));
      if (!used.has(candidate)) {
        used.add(candidate);
        snapped[i] = candidate;
        continue;
      }
      // Collision → search fallback grid for nearest unused slot.
      let best = -1;
      let bestDist = Infinity;
      for (let g = fallback; g < period; g += fallback) {
        if (used.has(g)) continue;
        const d = Math.abs(g - t);
        if (d < bestDist) {
          bestDist = d;
          best = g;
        }
      }
      if (best < 0) best = Math.max(fallback, Math.min(period - fallback, t));
      used.add(best);
      snapped[i] = best;
    }
    snapped.sort((a, b) => a - b);
    const next = snapped.map((c) => {
      const semi = Math.floor(c / 100);
      const cents = c - semi * 100;
      return { semitones: semi, cents };
    });
    updateSet(activeIndex, { scaleNotes: next });
  };

  const equalSpreadAcrossPeriod = () => {
    const n = active.scaleSize;
    const period = active.scalePeriod;
    const next: { semitones: number; cents: number }[] = [];
    for (let i = 0; i < n; i++) {
      const totalCents = (i * period) / n;
      const semi = Math.floor(totalCents / 100);
      const cents = totalCents - semi * 100;
      next.push({ semitones: semi, cents });
    }
    updateSet(activeIndex, { scaleNotes: next });
  };

  const changeScaleSize = (delta: 1 | -1) => {
    const newSize = Math.max(2, Math.min(24, active.scaleSize + delta));
    if (newSize === active.scaleSize) return;
    const period = active.scalePeriod;
    // Preserve existing intervals where possible: reuse the first
    // `min(old, newSize)` degrees, fill any extras by spreading evenly
    // across whatever range remains. Simpler than full equal-spread and
    // keeps scales mostly intact when the player nudges by one.
    const oldNotes = active.scaleNotes;
    const kept = oldNotes.slice(0, Math.min(oldNotes.length, newSize));
    const nextNotes = [...kept];
    if (newSize > kept.length) {
      const last = kept[kept.length - 1]!;
      const startCents = last.semitones * 100 + last.cents;
      const gap = (period - startCents) / (newSize - kept.length + 1);
      for (let j = kept.length; j < newSize; j++) {
        const totalCents = startCents + gap * (j - kept.length + 1);
        const semi = Math.floor(totalCents / 100);
        const cents = totalCents - semi * 100;
        nextNotes.push({ semitones: semi, cents });
      }
    }
    updateSet(activeIndex, {
      scaleSize: newSize,
      scaleNotes: nextNotes,
      lowestDegreeIdx: Math.min(active.lowestDegreeIdx ?? 0, newSize - 1),
    });
  };

  const stopPreview = () => {
    for (const id of previewTimers.current) window.clearTimeout(id);
    previewTimers.current = [];
    setPreviewPlaying(false);
  };

  const playPreview = () => {
    if (previewPlaying) {
      stopPreview();
      return;
    }
    const engine = ensureStarted();
    setPreviewPlaying(true);
    const STEP_MS = 200;
    // Envelope params for the flash visualization — matches the preview
    // voice's decay so the glow dies when the note dies.
    const env = { attackMs: 15, decayMs: 500, sustainFrac: 0.7, tailSec: 3.5 };
    derived.forEach((s, i) => {
      const id = window.setTimeout(() => {
        engine.pluck(s.stringIndex, s.freq, 0.72, `preview-${s.stringIndex}`);
        flashString(s.stringIndex, STEP_MS + 120);
        flashPluck(s.stringIndex, 0.72, performance.now(), null, env);
      }, i * STEP_MS);
      previewTimers.current.push(id);
    });
    const doneId = window.setTimeout(
      () => setPreviewPlaying(false),
      derived.length * STEP_MS + 200,
    );
    previewTimers.current.push(doneId);
  };

  const [shareStatus, setShareStatus] = useState<'idle' | 'copied' | 'error'>('idle');
  const copyShareLink = async (includeTuner: boolean) => {
    const url = buildTuningUrl(active, { includeTuner });
    try {
      await navigator.clipboard.writeText(url);
      setShareStatus('copied');
      setTimeout(() => setShareStatus('idle'), 1400);
    } catch {
      // Fallback — show the URL in a prompt so the user can copy it
      // manually on browsers that don't grant clipboard access.
      window.prompt('Copy this link:', url);
      setShareStatus('idle');
    }
  };

  return (
    <div className="tp">
      {/* Sets */}
      <section className="tp-sets">
        <div className="tp-sets__row">
          {sets.map((st, i) => {
            const isActive = i === activeIndex;
            const isEditing = editingName === i;
            return (
              <div
                key={i}
                className={'tp-set' + (isActive ? ' tp-set--active' : '')}
                onClick={() => !isEditing && setActive(i)}
                onDoubleClick={() => setEditingName(i)}
              >
                {isEditing ? (
                  <input
                    autoFocus
                    className="tp-set__name-edit"
                    defaultValue={st.name}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (v) renameSet(i, v);
                      setEditingName(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                      if (e.key === 'Escape') setEditingName(null);
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <div className="tp-set__name">{st.name}</div>
                )}
                <div className="tp-set__sub">{setSummary(st)}</div>
                {sets.length > 1 && isActive && (
                  <button
                    type="button"
                    className="tp-set__del"
                    title="Delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (confirm(`Delete "${st.name}"?`)) removeSet(i);
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            );
          })}
          <button
            type="button"
            className="tp-set tp-set--add"
            onClick={addSet}
            title="New set (copy of current)"
          >
            +
          </button>
        </div>
      </section>

      {/* Preset gallery — pick a shape, not a name */}
      <section>
        <h3 className="tp-h">Shape</h3>
        <PresetGrid
          activeKey={active.presetKey}
          onPick={(key) => applyPreset(activeIndex, key)}
        />
      </section>


      {/* Scale circle — root at 12 o'clock, tap dot to rotate it in */}
      <section>
        <div className="tp-pattern-head">
          <h3 className="tp-h">Pattern</h3>
          <div className="tp-count-nudge" title="Number of notes in the scale">
            <button
              type="button"
              className="tp-count-nudge__btn"
              onClick={() => changeScaleSize(-1)}
              disabled={active.scaleSize <= 2}
            >
              −
            </button>
            <span className="tp-count-nudge__n">{active.scaleSize}</span>
            <span className="tp-count-nudge__tag">notes</span>
            <button
              type="button"
              className="tp-count-nudge__btn"
              onClick={() => changeScaleSize(1)}
              disabled={active.scaleSize >= 24}
            >
              +
            </button>
          </div>
          <button
            type="button"
            className="tp-chip"
            onClick={equalSpreadAcrossPeriod}
            title="Rải đều N nốt qua toàn bộ period"
          >
            ≡ Equal
          </button>
          <button
            type="button"
            className="tp-chip"
            onClick={randomizeScale}
            title="Random các nốt trong period"
          >
            Random
          </button>
          <button
            type="button"
            className="tp-chip"
            onClick={() => cascadingSnap(200, 100)}
            title="Snap về bội 200¢; khi đụng nhau thì dùng 100¢"
          >
            200/100
          </button>
          <button
            type="button"
            className="tp-chip"
            onClick={() => cascadingSnap(100, 50)}
            title="Snap về semitone; đụng nhau dùng 50¢ (quartertone)"
          >
            100/50
          </button>
          <button
            type="button"
            className="tp-chip"
            onClick={() => cascadingSnap(50, 25)}
            title="Snap về 50¢; đụng nhau dùng 25¢"
          >
            50/25
          </button>
        </div>
        <ScaleCircle
          set={active}
          onSetBass={setBass}
          onSetLowest={setLowestDegree}
          onSetLowestOctave={setLowestOctave}
          onSetDegreeInterval={setDegreeInterval}
        />
        <div className="tp-register-row">
          <button
            type="button"
            className="tp-chip"
            onClick={centerToComfortRange}
            title="Shift register so the middle string sits near F#4 (most comfortable listening range)"
          >
            Center
          </button>
        </div>
      </section>

      {/* Strings — each string a coloured dot */}
      <section className="tp-strings">
        <div className="tp-strings__bar">
          <h3 className="tp-h">Strings</h3>
          <div className="tp-count-nudge">
            <button
              type="button"
              className="tp-count-nudge__btn"
              onClick={() => changeStringCount(-1)}
              disabled={active.stringCount <= MIN_STRINGS}
            >
              −
            </button>
            <span className="tp-count-nudge__n">{active.stringCount}</span>
            <button
              type="button"
              className="tp-count-nudge__btn"
              onClick={() => changeStringCount(1)}
              disabled={active.stringCount >= MAX_STRINGS}
            >
              +
            </button>
          </div>
          <button
            type="button"
            className={'tp-preview' + (previewPlaying ? ' tp-preview--on' : '')}
            onClick={playPreview}
            title="Pluck every string in order"
          >
            {previewPlaying ? '■ Stop' : '▶ Preview'}
          </button>
          <button
            type="button"
            className="tp-chip"
            onClick={() => copyShareLink(false)}
            title="Copy a link that loads this exact tuning"
          >
            {shareStatus === 'copied' ? 'Copied!' : 'Share link'}
          </button>
          <button
            type="button"
            className="tp-chip"
            onClick={() => copyShareLink(true)}
            title="Copy a link that opens the tuner with this exact tuning"
          >
            {shareStatus === 'copied' ? 'Copied!' : 'Share → Tuner'}
          </button>
          <label className="tp-free" title="Drag edits the scale (circle moves) vs. just one string">
            <input
              type="checkbox"
              checked={strictAscending}
              onChange={(e) => setStrictAscending(e.target.checked)}
            />
            <span>Strict ↑</span>
          </label>
          <label className="tp-free">
            <input
              type="checkbox"
              checked={freeCents}
              onChange={(e) => setFreeCents(e.target.checked)}
            />
            <span>µ-tone drag</span>
          </label>
          <div className="tp-seg" title="Layout của dây trên màn hình">
            <button
              type="button"
              className={'tp-seg__btn' + (stringSpacing === 'equal' ? ' tp-seg__btn--on' : '')}
              onClick={() => setStringSpacing('equal')}
            >
              Equal
            </button>
            <button
              type="button"
              className={'tp-seg__btn' + (stringSpacing === 'cents' ? ' tp-seg__btn--on' : '')}
              onClick={() => setStringSpacing('cents')}
            >
              Cents
            </button>
          </div>
          <button
            type="button"
            className="tp-chip"
            onClick={() => setStringOrderLowTop(!stringOrderLowTop)}
            title={stringOrderLowTop ? 'Lowest string ở trên' : 'Lowest string ở dưới (đàn thật)'}
          >
            {stringOrderLowTop ? '↓ low-top' : '↑ low-bottom'}
          </button>
        </div>
        <div className="tp-strings__list">
          {ordered.map((s) => (
            <StringRulerRow
              key={s.stringIndex}
              s={s}
              degree={s.stringIndex % active.scaleSize}
              scaleSize={active.scaleSize}
              freeCents={freeCents}
              onRetune={(pc, oct, c) => retuneDegree(s.stringIndex, pc, oct, c)}
              armed={armedStringIndex === s.stringIndex}
              onArm={() =>
                setArmedStringIndex((prev) =>
                  prev === s.stringIndex ? null : s.stringIndex,
                )
              }
              minMidi={rulerMinMidi}
              maxMidi={rulerMaxMidi}
            />
          ))}
        </div>
      </section>

      {/* Period — how wide the scale pattern spans before repeating.
          Most players want 1 oct; the tiles let curious users pick
          2 oct / 3 oct / tritave without typing cents numbers. */}
      <section>
        <h3 className="tp-h">Period</h3>
        <PeriodSlider set={active} setIndex={activeIndex} />
      </section>

      {/* Sing-to-capture — only surface if a string is armed. Keeps
          the panel clean when the player isn't using mic workflow. */}
      {armedStringIndex !== null && (
        <section>
          <h3 className="tp-h">Sing string {armedStringIndex + 1}</h3>
          <MicMode
            armedStringIndex={armedStringIndex}
            onCapture={({ pitchClass, octave, cents }) => {
              setStringOverride(activeIndex, armedStringIndex, {
                pitchClass,
                octave,
                cents,
              });
            }}
          />
        </section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function setSummary(st: TuningSet): string {
  const preset = st.presetKey ? getPresetByKey(st.presetKey)?.label : 'Custom';
  return `${st.bassPitchClass}${st.bassOctave} · ${preset} · ${st.stringCount}`;
}

/** Continuous period picker — a horizontal slider anchored by the
 *  common reference periods (1 oct, tritave, 2 oct, 3 oct, 4 oct) plus
 *  fine nudgers. Any value between 300¢ and 6000¢ is reachable. When
 *  the period changes we stretch `scaleNotes` so the interval shape is
 *  preserved, not crammed at the root. */
function PeriodSlider({ set, setIndex }: { set: TuningSet; setIndex: number }) {
  const updateSet = useTuningStore((s) => s.updateSet);
  const trackRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);

  const MIN = 300;
  const MAX = 6000;
  const anchors: { c: number; label: string }[] = [
    { c: 600, label: '½' },
    { c: 1200, label: '1 oct' },
    { c: 1902, label: 'Tritave' },
    { c: 2400, label: '2 oct' },
    { c: 3600, label: '3 oct' },
    { c: 4800, label: '4 oct' },
    { c: 6000, label: '5 oct' },
  ];

  const apply = (newPeriodCents: number) => {
    const target = Math.max(MIN, Math.min(MAX, Math.round(newPeriodCents)));
    const oldPeriod = set.scalePeriod > 0 ? set.scalePeriod : 1200;
    if (target === oldPeriod) return;
    const ratio = target / oldPeriod;
    const stretched = set.scaleNotes.map((n) => {
      const oldCents = n.semitones * 100 + n.cents;
      const newCents = oldCents * ratio;
      const semi = Math.floor(newCents / 100);
      const cents = newCents - semi * 100;
      return { semitones: semi, cents };
    });
    updateSet(setIndex, { scalePeriod: target, scaleNotes: stretched });
  };

  const pickFromPointer = (clientX: number) => {
    const el = trackRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
    const cents = MIN + (x / rect.width) * (MAX - MIN);
    apply(cents);
  };

  const pct = ((set.scalePeriod - MIN) / (MAX - MIN)) * 100;

  return (
    <div className="period-slider">
      <div className="period-slider__head">
        <span className="period-slider__value">
          {set.scalePeriod}¢
          <span className="period-slider__hint">
            {' · '}
            {set.scalePeriod === 1200
              ? '1 oct'
              : set.scalePeriod === 1902
                ? 'tritave (3:1)'
                : set.scalePeriod === 2400
                  ? '2 oct'
                  : set.scalePeriod === 3600
                    ? '3 oct'
                    : `${(set.scalePeriod / 1200).toFixed(2)} × oct`}
          </span>
        </span>
        <div className="period-slider__nudge">
          <button
            type="button"
            className="period-slider__nbtn"
            onClick={() => apply(set.scalePeriod - 10)}
            title="−10¢"
          >
            −
          </button>
          <button
            type="button"
            className="period-slider__nbtn"
            onClick={() => apply(set.scalePeriod + 10)}
            title="+10¢"
          >
            +
          </button>
        </div>
      </div>
      <div
        className="period-slider__track"
        ref={trackRef}
        onPointerDown={(e) => {
          draggingRef.current = true;
          (e.target as Element).setPointerCapture?.(e.pointerId);
          pickFromPointer(e.clientX);
        }}
        onPointerMove={(e) => {
          if (!draggingRef.current) return;
          if ((e.buttons & 1) === 0) {
            draggingRef.current = false;
            return;
          }
          pickFromPointer(e.clientX);
        }}
        onPointerUp={() => {
          draggingRef.current = false;
        }}
        onPointerCancel={() => {
          draggingRef.current = false;
        }}
      >
        {anchors.map((a) => {
          const left = ((a.c - MIN) / (MAX - MIN)) * 100;
          return (
            <div
              key={a.c}
              className="period-slider__anchor"
              style={{ left: `${left}%` }}
            >
              <div className="period-slider__anchor-tick" />
              <div className="period-slider__anchor-label">{a.label}</div>
            </div>
          );
        })}
        <div
          className="period-slider__marker"
          style={{ left: `${pct}%` }}
        />
      </div>
      <div className="period-slider__anchorbar">
        {anchors.map((a) => (
          <button
            key={a.c}
            type="button"
            className={
              'period-slider__chip' +
              (set.scalePeriod === a.c ? ' period-slider__chip--on' : '')
            }
            onClick={() => apply(a.c)}
          >
            {a.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export type { DerivedString };
